class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};

    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]'
    );

    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');

    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.resultsCloseTimer = null;

    this.setupEventListeners();
    this.updateSubmitAvailability();
  }

  setupEventListeners() {
    this.input.form.addEventListener(
      'submit',
      this.onFormSubmit.bind(this)
    );

    this.input.addEventListener(
      'focus',
      this.onFocus.bind(this)
    );

    this.addEventListener(
      'focusout',
      this.onFocusOut.bind(this)
    );

    this.addEventListener(
      'keyup',
      this.onKeyup.bind(this)
    );

    this.addEventListener(
      'keydown',
      this.onKeydown.bind(this)
    );

    this.addEventListener(
      'click',
      this.onResultClick.bind(this)
    );
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const newSearchTerm = this.getQuery();

    this.cancelResultsClose();

    if (!newSearchTerm.length) {
      this.close(true);
      return;
    }

    if (
      !this.searchTerm ||
      !newSearchTerm.startsWith(this.searchTerm)
    ) {
      this.querySelector(
        '#predictive-search-results-groups-wrapper'
      )?.remove();
    }

    this.searchTerm = newSearchTerm;

    /*
      Disable Enter and the search button while the new
      result request is still loading.
    */
    this.removeAttribute('results');
    this.updateSubmitAvailability();

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    /*
      Never submit the form to Shopify's broad /search page.
      The search icon only opens a product when exactly one
      product is currently showing.
    */
    event.preventDefault();
    this.openOnlyProduct();
  }

  onFormReset(event) {
    event.preventDefault();

    this.input.value = '';
    this.input.focus();

    this.toggleResetButton();
    this.close(true);
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (
      this.getAttribute('results') === 'true'
    ) {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    window.setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();

      this.switchOption(
        event.code === 'ArrowUp' ? 'up' : 'down'
      );
    }
  }

  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
      return;
    }

    if (event.code === 'Enter') {
      /*
        Enter does nothing when zero or multiple products
        are showing.
      */
      event.preventDefault();
      this.openOnlyProduct();
    }
  }

  onResultClick(event) {
    const suggestion = event.target.closest(
      '[data-predictive-search-suggestion]'
    );

    if (!suggestion) return;

    event.preventDefault();

    const suggestionText =
      suggestion.dataset.suggestion?.trim();

    if (!suggestionText) return;

    /*
      Clicking a suggestion refines the query inside the
      dropdown. It never opens a broad search page.
    */
    this.input.value = suggestionText;
    this.input.focus();

    this.toggleResetButton();

    this.searchTerm = '';
    this.onChange();
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const options = Array.from(
      this.querySelectorAll(
        '[data-predictive-search-option]'
      )
    ).filter(
      (element) => element.offsetParent !== null
    );

    if (!options.length) return;

    const selectedElement = this.querySelector(
      '[data-predictive-search-option][aria-selected="true"]'
    );

    const selectedIndex =
      options.indexOf(selectedElement);

    const moveUp = direction === 'up';

    let nextIndex;

    if (selectedIndex === -1) {
      nextIndex = moveUp
        ? options.length - 1
        : 0;
    } else if (moveUp) {
      nextIndex =
        selectedIndex === 0
          ? options.length - 1
          : selectedIndex - 1;
    } else {
      nextIndex =
        selectedIndex === options.length - 1
          ? 0
          : selectedIndex + 1;
    }

    selectedElement?.setAttribute(
      'aria-selected',
      'false'
    );

    const activeElement = options[nextIndex];

    activeElement.setAttribute(
      'aria-selected',
      'true'
    );

    this.input.setAttribute(
      'aria-activedescendant',
      activeElement.id
    );
  }

  getOnlyProductLink() {
    if (
      !this.getQuery().length ||
      this.getAttribute('results') !== 'true'
    ) {
      return null;
    }

    const productLinks = this.querySelectorAll(
      '[data-predictive-product-link]'
    );

    return productLinks.length === 1
      ? productLinks[0]
      : null;
  }

  openOnlyProduct() {
    const productLink =
      this.getOnlyProductLink();

    if (productLink) {
      productLink.click();
    }
  }

  updateSubmitAvailability() {
    const submitButton = this.querySelector(
      '.search__button'
    );

    if (!submitButton) return;

    const canSubmit = Boolean(
      this.getOnlyProductLink()
    );

    submitButton.setAttribute(
      'aria-disabled',
      canSubmit ? 'false' : 'true'
    );

    submitButton.dataset.productReady =
      canSubmit ? 'true' : 'false';
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm
      .replaceAll(' ', '-')
      .toLowerCase();

    this.setLiveRegionLoadingState();
    this.cancelResultsClose();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(
        this.cachedResults[queryKey]
      );

      return;
    }

    /*
      Cancel the previous request whenever the query changes.
      This prevents an older response replacing newer results.
    */
    this.abortController.abort();

    this.abortController =
      new AbortController();

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(
            response.status
          );

          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsSection =
          new DOMParser()
            .parseFromString(
              text,
              'text/html'
            )
            .querySelector(
              '#shopify-section-predictive-search'
            );

        if (!resultsSection) {
          throw new Error(
            'Predictive search section was not returned.'
          );
        }

        const resultsMarkup =
          resultsSection.innerHTML;

        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[
              queryKey
            ] = resultsMarkup;
          }
        );

        /*
          Ignore a response when the input has changed while
          the request was running.
        */
        if (this.getQuery() !== searchTerm) {
          return;
        }

        this.renderSearchResults(
          resultsMarkup
        );
      })
      .catch((error) => {
        if (
          error?.name === 'AbortError' ||
          error?.code === 20
        ) {
          return;
        }

        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        '.predictive-search-status'
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        'data-loading-text'
      );

    this.setLiveRegionText(
      this.loadingText
    );

    this.setAttribute(
      'loading',
      true
    );
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute(
      'aria-hidden',
      'false'
    );

    this.statusElement.textContent =
      statusText;

    window.setTimeout(() => {
      this.statusElement.setAttribute(
        'aria-hidden',
        'true'
      );
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    this.setAttribute(
      'results',
      'true'
    );

    this.highlightProductMatches();
    this.setLiveRegionResults();
    this.updateSubmitAvailability();

    const productCount =
      this.getProductCount();

    if (productCount > 0) {
      this.open();
    } else {
      this.close(false);
    }
  }

  highlightProductMatches() {
    const query = this.getQuery();

    if (!query.length) return;

    const escapedQuery = query.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&'
    );

    const matcher = new RegExp(
      escapedQuery,
      'gi'
    );

    this.querySelectorAll(
      '[data-predictive-product-title]'
    ).forEach((element) => {
      const title =
        element.dataset.productTitle ||
        element.textContent ||
        '';

      const fragment =
        document.createDocumentFragment();

      let lastIndex = 0;
      let match;

      matcher.lastIndex = 0;

      while (
        (match = matcher.exec(title)) !== null
      ) {
        if (match.index > lastIndex) {
          fragment.append(
            document.createTextNode(
              title.slice(
                lastIndex,
                match.index
              )
            )
          );
        }

        const mark =
          document.createElement('mark');

        mark.className =
          'predictive-search__match';

        mark.textContent = match[0];

        fragment.append(mark);

        lastIndex =
          match.index + match[0].length;

        if (match[0].length === 0) {
          matcher.lastIndex += 1;
        }
      }

      if (lastIndex === 0) {
        element.textContent = title;
        return;
      }

      if (lastIndex < title.length) {
        fragment.append(
          document.createTextNode(
            title.slice(lastIndex)
          )
        );
      }

      element.replaceChildren(fragment);
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');

    const countValue = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );

    this.setLiveRegionText(
      countValue?.textContent || ''
    );
  }

  getProductCount() {
    return this.querySelectorAll(
      '[data-predictive-product-link]'
    ).length;
  }

  updateSearchPanelHeight() {
    if (
      !this.hasAttribute('open') ||
      this.getProductCount() === 0
    ) {
      return;
    }

    const resultsContent = this.querySelector(
      '#predictive-search-results'
    );

    if (!resultsContent) return;

    const resultPanelStyles =
      window.getComputedStyle(
        this.predictiveSearchResults
      );

    const paddingTop =
      parseFloat(
        resultPanelStyles.paddingTop
      ) || 0;

    const paddingBottom =
      parseFloat(
        resultPanelStyles.paddingBottom
      ) || 0;

    /*
      Measure the new inner result content instead of the
      outer panel's previous fixed height.
    */
    const naturalHeight = Math.ceil(
      resultsContent.scrollHeight +
      paddingTop +
      paddingBottom
    );

    const maximumHeight = Math.max(
      0,
      window.innerHeight - 60
    );

    const resultsHeight = Math.min(
      naturalHeight,
      maximumHeight
    );

    this.predictiveSearchResults.style.setProperty(
      '--ns-search-results-height',
      `${resultsHeight}px`
    );

    this.closest(
      '.ns-site-header'
    )?.style.setProperty(
      '--ns-dropdown-glass-height',
      `${resultsHeight}px`
    );
  }

  open() {
    if (this.getProductCount() === 0) {
      return;
    }

    const panel = this.closest(
      '.search-modal__content'
    );

    const siteHeader = this.closest(
      '.ns-site-header'
    );

    this.cancelResultsClose();

    const wasOpen =
      this.hasAttribute('open');

    this.setAttribute('open', '');

    this.input.setAttribute(
      'aria-expanded',
      'true'
    );

    this.isOpen = true;

    if (!panel) {
      return;
    }

    panel.classList.remove(
      'ns-search-results-closing'
    );

    if (!wasOpen) {
      panel.classList.remove(
        'ns-search-results-visible'
      );

      this.predictiveSearchResults.style.setProperty(
        '--ns-search-results-height',
        '0px'
      );

      siteHeader?.style.setProperty(
        '--ns-dropdown-glass-height',
        '0px'
      );
    } else {
      panel.classList.add(
        'ns-search-results-visible'
      );
    }

    window.requestAnimationFrame(() => {
      if (
        !this.isOpen ||
        !this.hasAttribute('open')
      ) {
        return;
      }

      this.updateSearchPanelHeight();

      if (!wasOpen) {
        window.requestAnimationFrame(() => {
          if (
            !this.isOpen ||
            !this.hasAttribute('open')
          ) {
            return;
          }

          panel.classList.add(
            'ns-search-results-visible'
          );
        });
      }
    });

    window.setTimeout(() => {
      if (
        !this.isOpen ||
        !this.hasAttribute('open')
      ) {
        return;
      }

      this.updateSearchPanelHeight();

      panel.classList.add(
        'ns-search-results-visible'
      );
    }, 120);
  }

  close(
    clearSearchTerm = false,
    immediate = false
  ) {
    this.closeResults(
      clearSearchTerm,
      immediate
    );

    this.isOpen = false;
  }

  closeResults(
    clearSearchTerm = false,
    immediate = false
  ) {
    const panel = this.closest(
      '.search-modal__content'
    );

    const siteHeader = this.closest(
      '.ns-site-header'
    );

    this.cancelResultsClose();

    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';

      this.removeAttribute('results');

      this.abortController.abort();

      this.abortController =
        new AbortController();

      this.toggleResetButton();
      this.updateSubmitAvailability();
    }

    panel?.classList.remove(
      'ns-search-results-visible'
    );

    panel?.classList.add(
      'ns-search-results-closing'
    );

    this.predictiveSearchResults.style.setProperty(
      '--ns-search-results-height',
      '0px'
    );

    siteHeader?.style.setProperty(
      '--ns-dropdown-glass-height',
      '0px'
    );

    if (
      immediate ||
      !this.hasAttribute('open')
    ) {
      this.finishCloseResults(
        clearSearchTerm
      );

      return;
    }

    this.resultsCloseTimer =
      window.setTimeout(() => {
        this.finishCloseResults(
          clearSearchTerm
        );
      }, 340);
  }

  finishCloseResults(
    clearSearchTerm = false
  ) {
    const selected = this.querySelector(
      '[data-predictive-product-link][aria-selected="true"]'
    );

    selected?.setAttribute(
      'aria-selected',
      'false'
    );

    this.input.setAttribute(
      'aria-activedescendant',
      ''
    );

    this.input.setAttribute(
      'aria-expanded',
      'false'
    );

    this.removeAttribute('loading');
    this.removeAttribute('open');

    const panel = this.closest(
      '.search-modal__content'
    );

    panel?.classList.remove(
      'ns-search-results-visible',
      'ns-search-results-closing'
    );

    this.predictiveSearchResults.style.removeProperty(
      '--ns-search-results-height'
    );

    this.closest(
      '.ns-site-header'
    )?.style.removeProperty(
      '--ns-dropdown-glass-height'
    );

    if (clearSearchTerm) {
      this.predictiveSearchResults.innerHTML =
        '';
    }

    this.resultsMaxHeight = false;
    this.resultsCloseTimer = null;
  }

  cancelResultsClose() {
    if (!this.resultsCloseTimer) {
      return;
    }

    window.clearTimeout(
      this.resultsCloseTimer
    );

    this.resultsCloseTimer = null;
  }
}

customElements.define(
  'predictive-search',
  PredictiveSearch
);